import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./NotificationSettings.module.sass";
import Item from "../Item";
import Tooltip from "../../../components/Tooltip";
import { 
    isPushSupported, 
    isPushSubscribed, 
    subscribeToPushNotifications, 
    unsubscribeFromPushNotifications,
    getNotificationPermission 
} from "../../../services/pushNotification.service";
import { showSuccess, showError } from "../../../services/toast.service";

const NotificationSettings = ({ className }) => {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('default');

    useEffect(() => {
        const checkPushStatus = async () => {
            try {
                if (!isPushSupported()) {
                    setLoading(false);
                    return;
                }

                const permission = getNotificationPermission();
                setPermissionStatus(permission);

                if (permission === 'granted') {
                    const subscribed = await isPushSubscribed();
                    setPushEnabled(subscribed);
                }
            } catch (error) {
                console.error('Erreur vérification statut push:', error);
            } finally {
                setLoading(false);
            }
        };

        checkPushStatus();
    }, []);

    const handleTogglePush = async () => {
        if (toggling) return;

        setToggling(true);
        try {
            if (pushEnabled) {
                // Désactiver les notifications
                const result = await unsubscribeFromPushNotifications();
                if (result.success) {
                    setPushEnabled(false);
                    showSuccess('Notifications désactivées');
                } else {
                    showError(result.message || 'Erreur lors de la désactivation');
                }
            } else {
                // Activer les notifications
                const result = await subscribeToPushNotifications();
                if (result.success) {
                    setPushEnabled(true);
                    setPermissionStatus('granted');
                    showSuccess('Notifications activées');
                } else {
                    showError(result.message || 'Erreur lors de l\'activation');
                }
            }
        } catch (error) {
            console.error('Erreur toggle push:', error);
            showError('Une erreur est survenue');
        } finally {
            setToggling(false);
        }
    };

    // Si les notifications push ne sont pas supportées
    if (!isPushSupported()) {
        return (
            <Item
                className={cn(styles.card, className)}
                title="Notifications"
                classTitle="title-red"
            >
                <div className={styles.unsupported}>
                    Les notifications push ne sont pas supportées par votre navigateur.
                </div>
            </Item>
        );
    }

    // Si la permission a été refusée définitivement
    if (permissionStatus === 'denied') {
        return (
            <Item
                className={cn(styles.card, className)}
                title="Notifications"
                classTitle="title-red"
            >
                <div className={styles.denied}>
                    <span>Les notifications ont été bloquées par votre navigateur.</span>
                    <p className={styles.hint}>
                        Pour les réactiver, modifiez les paramètres de votre navigateur pour ce site.
                    </p>
                </div>
            </Item>
        );
    }

    return (
        <Item
            className={cn(styles.card, className)}
            title="Notifications"
            classTitle="title-red"
        >
            <div className={styles.fieldset}>
                <div className={styles.row}>
                    <div className={styles.label}>
                        Notifications push
                        <Tooltip
                            className={styles.tooltip}
                            title="Recevez des notifications même lorsque l'application n'est pas ouverte."
                            icon="info"
                            place="right"
                        />
                    </div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            checked={pushEnabled}
                            onChange={handleTogglePush}
                            disabled={loading || toggling}
                        />
                        <span className={styles.slider}></span>
                    </label>
                </div>
            </div>
        </Item>
    );
};

export default NotificationSettings;
